import { z } from 'zod';
import {
  FLIGHT_RISKS,
  POTENTIALS,
  TALENT_PERFORMANCE,
  TALENT_STATUSES,
} from './talent.js';

/** مخططات إدارة المواهب وشراكة الموارد البشرية. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ بصيغة سنة-شهر-يوم.');

/* ==========================================================================
   الجلسة
   ========================================================================== */

export const UpsertTalentReviewSchema = z.object({
  name: z.string().trim().min(3, 'اسم الجلسة قصير.').max(160),
  heldOn: dateOnly,
  notes: optionalText(4000),
});
export type UpsertTalentReviewInput = z.infer<typeof UpsertTalentReviewSchema>;

export const TalentStatusSchema = z.object({
  status: z.enum(TALENT_STATUSES),
});
export type TalentStatusInput = z.infer<typeof TalentStatusSchema>;

export const TalentReviewsQuerySchema = z.object({
  status: z.enum(TALENT_STATUSES).optional(),
});
export type TalentReviewsQuery = z.infer<typeof TalentReviewsQuerySchema>;

/* ==========================================================================
   الموضع في المصفوفة
   ========================================================================== */

/**
 * وضعُ اسمٍ في المصفوفة.
 *
 * والأداءُ يُرسَل ولا يُقرأ من دورة التقييم وقت العرض: الدوراتُ تُفتح
 * وتُغلق، وجلسةٌ عُقدت في مارس يجب أن تبقى قابلةً للقراءة كما كانت. ويُقترَح
 * عليه ما في آخر تقييمٍ أُرسل، ثمّ يُعدَّل باليد.
 */
export const UpsertAssessmentSchema = z.object({
  employeeId: Cuid,
  performance: z.enum(TALENT_PERFORMANCE),
  potential: z.enum(POTENTIALS).default('MEDIUM'),
  flightRisk: z.enum(FLIGHT_RISKS).default('LOW'),
  impactOfLoss: z.coerce
    .number()
    .int()
    .min(1, 'أثرُ فقده من واحدٍ إلى ثلاثة.')
    .max(3, 'أثرُ فقده من واحدٍ إلى ثلاثة.')
    .default(2),
  actions: optionalText(2000),
  notes: optionalText(2000),
});
export type UpsertAssessmentInput = z.infer<typeof UpsertAssessmentSchema>;

/* ==========================================================================
   شركاء الموارد البشرية
   ========================================================================== */

export const UpsertPartnerSchema = z.object({
  orgUnitId: Cuid,
  partnerId: Cuid,
  since: dateOnly,
  notes: optionalText(2000),
});
export type UpsertPartnerInput = z.infer<typeof UpsertPartnerSchema>;
